import { useEffect } from 'react'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { AppHeader } from '@/components/AppHeader'
import { useAuth } from '@/hooks/useAuth'
import { usePlatform } from '@/hooks/usePlatform'
import { DEVICE_PRESETS } from '@busswerks/shared'

// Round the preset count down to the nearest 5 so the headline copy stays
// stable between minor catalog updates (e.g. adding 3 presets shouldn't
// immediately change "80+ presets" to "83+ presets").
const PRESET_COUNT_LABEL = `${Math.floor(DEVICE_PRESETS.length / 5) * 5}+`

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
})

function LandingPage() {
  const { isAuthenticated, isLoading } = useAuth()
  const platform = usePlatform()
  const navigate = useNavigate()

  // Native shells (Capacitor iOS/iPadOS/Mac Catalyst, future Android)
  // never show the marketing landing page — it's a web funnel surface
  // for unauthenticated visitors, not an in-app destination. Redirect
  // to the authenticated Home or the login page as soon as useAuth
  // settles. `replace: true` keeps the back stack clean — the WebView
  // shouldn't be able to swipe-back into the landing page after login.
  useEffect(() => {
    if (!platform.isNative) return
    if (isLoading) return
    void navigate({
      to: isAuthenticated ? '/home' : '/login',
      replace: true,
    })
  }, [platform.isNative, isAuthenticated, isLoading, navigate])

  // Short-circuit the landing render on native so the marketing hero
  // doesn't flash into the webview between mount and the redirect
  // above. Plain centered spinner keeps the splash-to-app transition
  // feeling seamless.
  if (platform.isNative) {
    return (
      <div className="min-h-[100dvh] grid place-items-center bg-surface-base text-content-tertiary text-sm">
        Loading…
      </div>
    )
  }

  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-surface-base to-surface-raised text-content-primary">
      <AppHeader />

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 sm:pb-16 text-center">
        <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.1] mb-5">
          Your whole studio,
          <br />
          <span className="text-accent">in one browser tab</span>
        </h2>
        <p className="text-lg sm:text-xl text-content-secondary max-w-2xl mx-auto mb-4 leading-relaxed">
          Two apps for music producers and audio engineers:{' '}
          <span className="text-content-primary font-semibold">Bussboard</span>{' '}
          for mapping any signal-flow rig — studio, live stage, broadcast truck,
          camera + lighting — on a node-graph canvas, and{' '}
          <span className="text-content-primary font-semibold">Werkshop</span> —
          a full DAW with a step sequencer, piano roll, automation lanes, a
          sampler, first-party creative synths, per-bus FX, MIDI routing, and
          WAV export.
        </p>
        <p className="text-sm text-content-tertiary max-w-2xl mx-auto mb-8">
          <Link
            to="/roadmap"
            className="underline underline-offset-4 hover:text-content-primary transition-colors duration-150"
          >
            See what&apos;s shipping next →
          </Link>
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          {isAuthenticated ? (
            <Link
              to="/home"
              className="w-full sm:w-auto btn-primary btn-lg rounded-xl font-semibold text-lg shadow-medium"
            >
              Go to Home
            </Link>
          ) : (
            <Link
              to="/login"
              className="w-full sm:w-auto btn-primary btn-lg rounded-xl font-semibold text-lg shadow-medium"
            >
              Get Started Free
            </Link>
          )}
          <Link
            to="/bussboard/$id"
            params={{ id: 'local-dev' }}
            className="text-sm text-content-tertiary hover:text-content-primary underline underline-offset-4 transition-colors duration-150"
          >
            or try the Bussboard demo
          </Link>
          <Link
            to="/werkshop/projects/$id"
            params={{ id: 'local-jam' }}
            className="text-sm text-content-tertiary hover:text-content-primary underline underline-offset-4 transition-colors duration-150"
          >
            or jam in the sequencer
          </Link>
        </div>
        {!isAuthenticated && (
          <p className="mt-4 text-xs text-content-tertiary">
            Sequencer + MIDI Router free forever. No credit card required.
          </p>
        )}
      </section>

      {/* Three apps */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-content-tertiary text-center mb-2">
          Two apps, one workspace
        </h3>
        <p className="text-xs text-content-tertiary text-center mb-8">
          Plus more in active development —{' '}
          <Link
            to="/roadmap"
            className="underline underline-offset-4 hover:text-content-primary"
          >
            view the public roadmap
          </Link>
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          <StepCard
            step="B"
            title="Bussboard"
            description={`Map any signal-flow rig — studio, live stage, broadcast truck, camera + lighting — on a node-graph canvas. ${PRESET_COUNT_LABEL} device presets, zones, and AI-powered routing suggestions.`}
          />
          <StepCard
            step="W"
            title="Werkshop"
            description="Full DAW in the browser — step sequencer, piano roll, automation lanes, sampler + Slicer, first-party creative synths (Deep Sub / Pluck Pad / Drone), per-bus FX chains, MIDI router, and WAV export."
          />
        </div>
      </section>

      {/* Features */}
      <section className="bg-surface-sunken/60 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h3 className="text-2xl sm:text-3xl font-bold text-center mb-10">
            Built for how producers actually work
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <FeatureCard
              title="Visual Node Canvas"
              description="Interactive drag-and-drop canvas powered by React Flow. See every device and connection at a glance."
            />
            <FeatureCard
              title={`${PRESET_COUNT_LABEL} Device Presets`}
              description="Interfaces, mixers, synths, effects, monitors, and MIDI controllers — all with accurate port definitions."
            />
            <FeatureCard
              title="Web Audio Kits + FX"
              description="Four bundled kits (Nate08, Warehouse, Crate, Prism) plus a six-pad master FX bank — filter, delay, reverb, bitcrush, tape stop, stutter."
            />
            <FeatureCard
              title="MIDI Out + Sync"
              description="Any routable MIDI output becomes a sequencer destination. Your rack gets driven by the same grid you're editing on screen."
            />
            <FeatureCard
              title="AI Routing Suggestions"
              description="Connection recommendations powered by Claude, grounded in your actual device catalog. Available on paid plans."
            />
            <FeatureCard
              title="Cloud Sync + Sharing"
              description="Environments, projects, and samples saved automatically. Share with collaborators in one click; commercial-tier modules add SSO + pooled billing."
            />
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <h3 className="text-2xl sm:text-3xl font-bold text-center mb-3">
          Free core. Pay for the modules you actually want.
        </h3>
        <p className="text-center text-content-secondary mb-10 text-sm max-w-2xl mx-auto">
          The Sequencer and MIDI Router are free forever. Every other
          instrument, tool, and effect — Sampler, Deep Sub, Pluck Pad, Drone,
          Piano Roll, Automation — is licensed per-module at either a Personal
          or Commercial tier. Buy only what you use.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <PricingCard
            tier="Free core"
            price="$0"
            period="forever"
            features={[
              'Sequencer (pads, patterns, banks)',
              'MIDI Router',
              'Built-in kit voices + FX bank',
              'Bussboard (node-graph canvas)',
              'No credit card required',
            ]}
          />
          <PricingCard
            tier="Personal module"
            price="from $3"
            period="/mo or $29 once"
            featured
            features={[
              'Per-module license: synth / sampler / tool',
              'Personal-use projects + releases',
              'Monthly, annual, or one-time purchase',
              'Mix and match — buy what you use',
              'Free core stays free',
            ]}
          />
          <PricingCard
            tier="Commercial module"
            price="from $9"
            period="/mo or $99 once"
            features={[
              'Same modules as Personal',
              'Commercial-use license',
              'Studio, agency, and client-work rights',
              'Priority support',
            ]}
          />
        </div>
        <p className="text-center text-[11px] text-content-tertiary mt-4">
          Annual billing saves ~17% on subscription modules. Checkout is on the
          way — you can browse the full module catalog + prices in the Store
          today.
        </p>
        <div className="mt-6 mx-auto max-w-2xl rounded-xl border border-accent/30 bg-accent-muted/40 px-4 py-3 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-accent mb-1">
            Founding member price lock
          </p>
          <p className="text-xs text-content-secondary leading-relaxed">
            Subscribe before a new app ships and your bundle price is locked for
            the lifetime of your subscription — even as the catalog grows.{' '}
            <Link
              to="/roadmap"
              className="underline underline-offset-4 hover:text-content-primary"
            >
              See what&apos;s next →
            </Link>
          </p>
        </div>
        <div className="text-center mt-8">
          <Link
            to={isAuthenticated ? '/home' : '/login'}
            className="btn-primary btn-lg rounded-xl font-semibold shadow-medium"
          >
            {isAuthenticated ? 'Go to Home' : 'Get Started Free'}
          </Link>
        </div>
      </section>

      <footer className="border-t border-brd-subtle mt-4 py-6 sm:py-8 text-content-tertiary text-center text-xs sm:text-sm px-4">
        <p>Busswerks — Visual audio routing for every studio</p>
      </footer>
    </div>
  )
}

function StepCard({
  step,
  title,
  description,
}: {
  step: string
  title: string
  description: string
}) {
  return (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent text-white font-bold text-sm mb-4">
        {step}
      </div>
      <h4 className="text-lg font-semibold mb-2">{title}</h4>
      <p className="text-sm text-content-secondary leading-relaxed">
        {description}
      </p>
    </div>
  )
}

function FeatureCard({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <div className="bg-surface-raised rounded-xl p-5 border border-brd-subtle card-interactive">
      <h4 className="font-semibold mb-1.5">{title}</h4>
      <p className="text-sm text-content-secondary leading-relaxed">
        {description}
      </p>
    </div>
  )
}

function PricingCard({
  tier,
  price,
  period,
  features,
  featured,
}: {
  tier: string
  price: string
  period: string
  features: string[]
  featured?: boolean
}) {
  return (
    <div
      className={`rounded-xl border p-5 transition-all duration-150 ${
        featured
          ? 'border-accent bg-accent-muted shadow-medium ring-1 ring-accent/20'
          : 'border-brd-subtle hover:border-brd-emphasis'
      }`}
    >
      {featured && (
        <div className="text-[10px] font-semibold uppercase tracking-wider text-accent mb-2">
          Most popular
        </div>
      )}
      <div className="text-sm font-semibold text-content-primary">{tier}</div>
      <div className="flex items-baseline gap-1 mt-2">
        <span className="text-3xl font-bold text-content-primary">{price}</span>
        <span className="text-xs text-content-tertiary">{period}</span>
      </div>
      <ul className="mt-4 space-y-2">
        {features.map(f => (
          <li
            key={f}
            className="flex items-start gap-2 text-sm text-content-secondary"
          >
            <span className="text-success mt-0.5 flex-shrink-0">&#10003;</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
